import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, AdminState, Field, Notice, SkeletonList, isForbidden } from '@/admin/AdminShell'
import { RecordPicker } from '@/card/RecordPicker'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  assignInbound,
  dismissInbound,
  ticketFromInbound,
  fetchInboxQueue,
  type AssignTarget,
  type QueueItem,
} from './inboxApi'

/**
 * Очередь разбора входящих писем (тикет #121).
 *
 * Сюда попадает письмо, отправителя которого система не нашла среди контактов и
 * контрагентов, — или нашла у нескольких сразу. Само оно никуда не кладётся: подсказка по
 * домену почты показана словами, но коллега клиента с тем же доменом — ещё не клиент, и
 * решает это человек.
 *
 * Право — `mail.inbox`: `read` видит очередь, `update` прикрепляет и отклоняет. Чьи ящики
 * видны, решает сервер по области права (общие — только со scope `all`); экран просто
 * показывает то, что пришло, и отказ называет словами.
 */
const TARGETS: { type: AssignTarget; entity: string; search: string; extra?: string }[] = [
  { type: 'counterparty', entity: 'sales.counterparty', search: 'name', extra: 'inn' },
  { type: 'contact', entity: 'sales.contact', search: 'last_name', extra: 'first_name' },
  { type: 'deal', entity: 'sales.deal', search: 'number', extra: 'title' },
]

export function InboxQueuePage() {
  const { t, user } = useApp()
  const queue = useQuery({ queryKey: ['mail-inbox'], queryFn: ({ signal }) => fetchInboxQueue(signal) })
  const mayDecide = can(user, 'mail.inbox', 'update')
  const items = queue.data ?? []

  return (
    <AdminPage
      titleKey="inbox.title"
      leadKey="inbox.lead"
      sectionKey="nav.section.work"
      count={queue.data ? items.length : undefined}
      readOnly={!mayDecide}
      roKey="inbox.ro"
      forbidden={isForbidden(queue.error)}
    >
      <AdminState
        isLoading={queue.isLoading}
        error={queue.error}
        onRetry={() => void queue.refetch()}
        forbiddenTitleKey="inbox.forbidden"
        errorTitleKey="inbox.error"
        skeleton={<SkeletonList rows={4} />}
      >
        {items.length === 0 ? (
          <StateView kind="empty" boxed compact title={t('inbox.empty')} description={t('inbox.empty.text')} />
        ) : (
          <ul className="stack" style={{ gap: 12, listStyle: 'none', padding: 0 }} aria-label={t('inbox.title')}>
            {items.map((item) => <QueueRow key={item.id} item={item} mayDecide={mayDecide} />)}
          </ul>
        )}
      </AdminState>
    </AdminPage>
  )
}

function QueueRow({ item, mayDecide }: { item: QueueItem; mayDecide: boolean }) {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<AssignTarget>('counterparty')
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(
    item.suggestedCounterpartyId != null
      ? { id: item.suggestedCounterpartyId, label: item.suggestedCounterpartyName ?? item.suggestedCounterpartyId }
      : null,
  )
  const [notice, setNotice] = useState<string | null>(null)
  const done = () => {
    setNotice(null)
    void queryClient.invalidateQueries({ queryKey: ['mail-inbox'] })
  }
  const fail = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const assign = useMutation({
    mutationFn: (record: { id: string }) => assignInbound(item.id, target, record.id),
    onSuccess: done,
    onError: fail,
  })
  const dismiss = useMutation({ mutationFn: () => dismissInbound(item.id), onSuccess: done, onError: fail })
  const mayOpenTicket = can(user, 'support.ticket', 'create')
  const toTicket = useMutation({
    mutationFn: (record: { id: string }) => ticketFromInbound(item.id, record.id),
    onSuccess: () => {
      done()
      void queryClient.invalidateQueries({ queryKey: ['list', 'support.ticket'] })
    },
    onError: fail,
  })
  const spec = TARGETS.find((candidate) => candidate.type === target) ?? TARGETS[0]!
  const sender = item.fromName ? `${item.fromName} <${item.fromAddress ?? ''}>` : item.fromAddress ?? t('inbox.noSender')
  const pickerId = `inbox-${item.id}`

  return (
    <li className="panel stack" style={{ gap: 8 }} aria-label={item.subject ?? t('mail.noSubject')}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="strong">{item.subject ?? t('mail.noSubject')}</span>
        <span className="muted t-sm">{sender}</span>
        <span className="spacer" />
        <span className="muted t-sm">{item.mailboxName} · {formatMoment(item.receivedAt, locale)}</span>
      </div>
      {item.excerpt && <p className="t-sm" style={{ margin: 0 }}>{item.excerpt}</p>}
      {item.reason && <span className="t-sm muted" role="note">{item.reason}</span>}
      {(item.attachments ?? []).length > 0 && (
        <ul className="t-sm" aria-label={t('inbox.files')} style={{ margin: 0 }}>
          {(item.attachments ?? []).map((file) => (
            <li key={file.id}>{file.fileName} — {t(`timeline.scan.${file.scanStatus}`)}</li>
          ))}
        </ul>
      )}
      {item.skippedAttachments && (
        <span className="t-sm" role="note">{t('inbox.skipped', { files: item.skippedAttachments })}</span>
      )}
      {item.suggestedCounterpartyId != null && (
        <span className="t-sm">{t('inbox.suggestion', { name: item.suggestedCounterpartyName ?? '' })}</span>
      )}
      {mayDecide && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label={t('inbox.attachTo')} htmlFor={`${pickerId}-type`}>
            <select id={`${pickerId}-type`} className="input" value={target} onChange={(event) => {
              setTarget(event.target.value as AssignTarget)
              setPicked(null)
            }}>
              {TARGETS.map((candidate) => (
                <option key={candidate.type} value={candidate.type}>{t(`inbox.target.${candidate.type}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t(`inbox.target.${target}`)} htmlFor={pickerId}>
            <RecordPicker id={pickerId} entity={spec.entity} searchColumn={spec.search} extraColumn={spec.extra}
                          value={picked} onChange={setPicked} />
          </Field>
          <button type="button" className="btn btn-primary" disabled={picked == null || assign.isPending}
                  onClick={() => picked && assign.mutate(picked)}>
            {t('inbox.attach')}
          </button>
          {/* Обращение из письма (#123) — на выбранного контрагента, в ту же очередь, что из
              кабинета. Только для контрагента: обращение заводится на клиента, а не на сделку. */}
          {target === 'counterparty' && mayOpenTicket && (
            <button type="button" className="btn btn-secondary" disabled={picked == null || toTicket.isPending}
                    onClick={() => picked && toTicket.mutate(picked)}>
              {t('inbox.toTicket')}
            </button>
          )}
          <button type="button" className="btn btn-ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
            {t('inbox.dismiss')}
          </button>
        </div>
      )}
      <Notice text={notice} />
    </li>
  )
}

function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}
